import styled from 'styled-components';
import { Link } from 'react-router';
import { media, truncate } from 'utils/styledHelpers';

const StyledCampaignLayoutWrapper = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 1.14rem;
`;
const StyledCampaignMain = styled.div`
  width: calc(100% - 328px - 16px);
  @media (max-width: 1200px) {
    width: 100%;
  }
`;
const StyledCampaignSidebar = styled.div`
  width: 328px;
  @media (max-width: 1200px) {
    max-width: 338px;
    width: 100%;
    height: 100%;
    max-height: calc(100vh - 5.85rem);
    top: 5.85rem;
    position: fixed;
    right: 0;
    border-radius: 0.71rem 0.71rem 0 0;
    transform: translateX(0);
    background-color: ${(props) => props.theme.colors.secondaryBg};
    padding: 3.85rem 0 1.4rem 1rem;
    transition: 0.33s;
    animation: ${(props) => (props.$isCollapsed ? 'closeSidebar .33s ease-in-out forwards;' : ' showSidebar .33s ease-in-out forwards;')};
    z-index: 100;
    box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
  }

  @keyframes showSidebar {
    0% {
      transform: translateX(100%);
    }
    100% {
      transform: translateX(0px);
    }
  }
  @keyframes closeSidebar {
    0% {
      transform: translateX(0px);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const CloseButton = styled.span`
  display: none;
  @media (max-width: 1200px) {
    padding: 0 0 1.4rem 0;
    position: fixed;
    top: 1.4rem;
    right: 1.4rem;
    display: block;
  }
`;

const StyledSidebarIcon = styled.span`
  display: none;
  position: fixed;
  right: 0.43rem;
  padding: 0.71rem;
  border-radius: 0.71rem 0 0 0.71rem;
  top: 5.85rem;
  z-index: 11;
  background-color: ${(props) => props.theme.colors.secondaryBg};
  cursor: pointer;
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  @media (max-width: 1200px) {
    display: block;
  }
`;

const StyledCampaignItemWrapper = styled.div`
  position: relative;
  padding: ${(props) => (props.$smallPadding ? '1.71rem 1.14rem' : '1.71rem')};
  background-color: ${(props) => props.theme.colors.secondaryBg};
  border: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  margin-bottom: 1.14rem;
  transition: 0.3s opacity;

  ${(props) => props.$disabled
    && `
    opacity: 0.5;
    pointer-events: none;
    user-select: none;
  `}
`;

const StyledCampaignMainConfigWrapper = styled.div`
  margin: ${(props) => (props.$hasMargin ? '1.71rem 0' : 0)};

  & > strong {
    display: inline-block;
    margin-bottom: 1.71rem;
  }
`;
const StyledParticipantsResultWrapper = styled.div`
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  background-color: ${(props) => props.theme.colors.primaryBg};
  padding: 1.14rem;
  min-height: 5.7rem; // For loading state
  margin-bottom: 0.71rem;
`;
const StyledPlayersResultItem = styled.div`
  position: relative;
  border-left: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  padding: 0.9rem 0 0.9rem 1.8rem;
  > span {
    font-size: ${(props) => props.theme.typography.fontSizeSmall};
    color: ${(props) => props.theme.colors.filterLabelColor};
    margin-right: 1rem;
  }
`;
const StyledConditionsViewHeadingAndActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2.5rem;
  margin-bottom: ${(props) => (props.$noMargin ? '0' : '1.14rem')};
`;
const StyledPromocodeViewMainWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: ${(props) => (props.$vAlignCenter ? 'center' : 'flex-start')};
  gap: 2.5rem;
  color: ${(props) => props.theme.colors.primaryText};
  @media (max-width: 550px) {
    flex-direction: column;
    gap: 0.71rem;
  }
`;
const StyledResultDataList = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  width: 100%;
  gap: 0.72rem 2.8rem;
`;
const StyledResultDataListItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.55rem 0 0.55rem 1.72rem;
  border-left: 1px solid ${(props) => props.theme.colors.secondaryBorder};

  > div:first-child {
    font-size: ${(props) => props.theme.typography.fontSizeSmall};
    color: ${(props) => props.theme.colors.filterLabelColor};
    margin-bottom: 2px;
  }
  @media (max-width: 1400px) {
    width: calc(50% - 1.4rem);
  }
  @media (max-width: 550px) {
    border-top: 1px solid ${(props) => props.theme.colors.secondaryBorder};
    border-left: none;
    padding-left: 0.55rem;
  }
`;
const StyledParticipantsResultHeading = styled.div`
  position: relative;
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => props.theme.colors.primaryText};
  border-left: none;
  padding: 1.12rem 0 1.12rem 1.71rem;
  flex-shrink: 0;
  &:before {
    position: absolute;
    content: '';
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1.14rem;
    border-bottom: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  }
`;
const StyledPlayerListTotalsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 0.71rem;
  > div {
    margin-bottom: 0;
  }
`;
const StyledGrayOptionsWithLabel = styled.div`
  display: flex;
  gap: 1.14rem 0.57rem;
  ${media.xs`
     flex-wrap: wrap;
  `}

  span {
    flex-shrink: 0;
    display: inline-block;
    font-weight: ${(props) => (props.$strongLabel ? '500' : '400')};
    color: ${(props) => (props.$strongLabel ? props.theme.colors.black : props.theme.colors.filterLabelColor)};
    padding: 0.27rem 0.85rem;
  }
  > div {
    color: ${(props) => props.theme.colors.black};
  }
`;

const StyledParticipantResultActions = styled.div`
  display: flex;
  align-items: center;
  row-gap: 1.14rem;
`;
const StyledResultPlayersView = styled.div`
  display: flex;
  border-left: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  padding: 0.8rem 0 0.8rem 1.72rem;
`;
const StyledPlayerViewCount = styled.span`
  padding: 0.28rem 0.57rem;
  margin-right: 0.85rem;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  font-size: 1rem;
  color: ${(props) => (props.isMissing ? props.theme.colors.danger : props.theme.colors.blue)};
  background-color: ${(props) => (props.isMissing ? `${props.theme.colors.tableTrHoverColor}` : `${props.theme.colors.blue}20`)};
`;

const StyledBonusSetupHintWrapper = styled.div`
  padding: 5.42rem 2rem 6.42rem;
  text-align: center;
  img {
    margin-bottom: 1.71rem;
  }
  h4 {
    margin-top: 0.57rem;
  }
`;

const StyledBonusSetupWrapper = styled.div`
  margin-top: -1.71rem;
  .ant-radio-group {
    display: flex;
    flex-wrap: wrap;
    row-gap: 1rem;
    column-gap: 1rem;
  }
`;

// Sidebar
const StyledSelectedValues = styled.div`
  ${(props) => props.$hasBtn
    && `
    display: flex;
    justify-content: space-between;
    gap: 0.71rem;
  `}
  > span:not(:last-of-type) {
    border-right: 1px solid ${(props) => props.theme.colors.primaryBorder};
    padding-right: 1.14rem;
    margin-right: 1.14rem;
  }

  > span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: ${(props) => props.theme.typography.fontWeightMedium};
    color: ${(props) => props.theme.colors.primaryText};
  }
`;
const StyledSelectedValueSpecification = styled.span`
  color: ${(props) => props.theme.colors.filterLabelColor};
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
`;
const StyledEditIcon = styled.div`
  padding: 0.57rem;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$disabled ? '.5' : '1')};
  > * {
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  }
`;
const StyledMarginTop = styled.div`
  margin-top: ${(props) => (props.$marginTop ? props.$marginTop : '2.28rem')};

  .ant-radio-group {
    width: 100%;

    .ant-radio-wrapper {
      margin-right: 1.28rem;
    }
  }
`;
const StyledRadioGroup = styled.div`
  margin-bottom: ${(props) => (props.$hasMargin ? '2.5rem' : '0')};

  .ant-radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 1.71rem 0.71rem;

    .ant-radio-wrapper {
      margin-right: 0;
    }
  }
`;
const StyledRadioItemComponent = styled.div`
  label.ant-radio-wrapper {
    width: 100%;
    min-height: 6.5rem;
    align-items: flex-start;
    padding: 1.14rem;
    margin: 0;
    border: 1px solid ${(props) => props.theme.colors.secondaryBorder};
    border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
    transition: 0.2s all;

    &.ant-radio-wrapper-checked {
      box-shadow: 0px 4px 6px 2px ${(props) => props.theme.colors.blue}15;
      border-color: ${(props) => props.theme.colors.blue};
    }

    .ant-radio {
      margin-right: 0.28rem;
      align-self: start;
    }
    &:not(:last-child) {
      margin-bottom: 0.57rem;
    }
  }
`;
const StyledRadioItemValue = styled.p`
  font-size: ${(props) => props.theme.typography.fontSizeDefault};
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  margin-bottom: 0.57rem;
  word-break: break-word;
`;
const StyledRadioItemDescription = styled.h4`
  font-size: ${(props) => props.theme.typography.fontSizeDefault};
`;

const StyledBonusTypeGroup = styled.div`
  &:not(:last-child) {
    margin-bottom: 2.38rem;
  }
  h4 {
    font-weight: ${(props) => props.theme.typography.fontWeightMedium};
    margin-bottom: 1.23rem;
  }
`;
const StyledBonusGroupWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.2rem 2.4rem;
`;
const StyledBonusItemWrapper = styled.div`
  width: 70px;
  text-align: center;
  font-size: ${(props) => props.theme.typography.fontSizeDefault};
  color: ${(props) => props.theme.colors.primaryText};
  opacity: ${(props) => (props.$disabled ? '0.5' : '1')};
`;
const StyledBonusItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 70px;
  border: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  margin-bottom: 0.85rem;
  transition: 0.3s all;

  svg {
    transition: 0.3s all cubic-bezier(0.67, -0.06, 0.27, 2.18);
    g path,
    path {
      fill: ${(props) => props.theme.colors.secondaryColor};
    }
  }

  ${(props) => !props.$disabled
    && `
    &:hover {
      border-color: ${props.theme.colors.blue};
      color: ${props.theme.colors.hover};
      box-shadow: 0 6px 10px ${props.theme.colors.blue}30;
      
      svg {
        transform: scale(1.2);
        g path, path {
          fill: ${props.theme.colors.blue};
        }
      }
    }
  `}

  ${(props) => props.$isSelected
    && `
    background: linear-gradient(137.6deg, ${props.theme.colors.blue}40 4%,${props.theme.colors.blue}05 96%);
    border-color: ${props.theme.colors.blue}68;
    color: ${props.theme.colors.hover};
    box-shadow: 0 6px 10px ${props.theme.colors.blue}30;
    svg {
      transform: scale(1.2);
      g path, path {
        fill: ${props.theme.colors.blue};
      }
    }
    
    & + span {
      color: ${props.theme.colors.blue};
    }
  `}
`;

const StyledCampaignReportBonusName = styled(Link)`
  display: block;
  color: ${(props) => props.theme.colors.blue};
  ${truncate()}
  &:hover {
    text-decoration: underline;
    color: ${(props) => props.theme.colors.blue};
  }
`;

const StyledWidgetsIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  svg:hover {
    cursor: pointer;
  }
  > div {
    display: flex;
    align-items: center;
    span {
      margin-right: 0.57rem;
    }
  }
  span {
    margin: 0;
    line-height: 1.71rem;
    color: ${(props) => props.theme.colors.primaryText};
  }
`;

export {
  StyledCampaignLayoutWrapper,
  StyledCampaignMain,
  StyledCampaignSidebar,
  StyledCampaignItemWrapper,
  StyledCampaignMainConfigWrapper,
  StyledParticipantsResultWrapper,
  StyledPlayersResultItem,
  StyledConditionsViewHeadingAndActions,
  StyledPromocodeViewMainWrapper,
  StyledResultDataList,
  StyledResultDataListItem,
  StyledParticipantsResultHeading,
  StyledPlayerListTotalsWrapper,
  StyledGrayOptionsWithLabel,
  StyledParticipantResultActions,
  StyledResultPlayersView,
  StyledPlayerViewCount,
  StyledBonusSetupHintWrapper,
  StyledBonusSetupWrapper,
  StyledSelectedValues,
  StyledSelectedValueSpecification,
  StyledEditIcon,
  StyledMarginTop,
  StyledRadioGroup,
  StyledRadioItemComponent,
  StyledRadioItemValue,
  StyledRadioItemDescription,
  StyledBonusTypeGroup,
  StyledBonusGroupWrapper,
  StyledBonusItemWrapper,
  StyledBonusItem,
  StyledCampaignReportBonusName,
  StyledWidgetsIcon,
  CloseButton,
  StyledSidebarIcon,
};
